// The cards (IMPLEMENTATION §2.6 "Sheets and cards", §2.7 "Tap semantics", UX §2 Info / Machine / Gallery /
// Crate / Ghost / Expansion cards): one card at a time, rising from the top edge of the tab bar. Info-type
// cards are counter + tabs tall (their essentials: a head row and one row of actions); "More" grows them to
// sheets.moreRatio·Hs for the rest; the Gallery card is clamp(sheets.galleryHsRatio·Hs, …). The models
// (ui/cards/model.ts) say what to show and which command each button sends; the card is non-modal (a tap on
// the board closes it and still acts) and hides when a drag starts.
import type { JSX } from 'preact';
import type { Command } from '../../sim/commands.ts';
import type { BType } from '../../sim/gen/ids.ts';
import { canUndoSell } from '../../sim/query.ts';
import { box } from '../components/box.ts';
import { Icon } from '../components/Icon.tsx';
import { fillCopy } from '../copy.ts';
import { shellCtx } from '../ctx.ts';
import { money } from '../format.ts';
import { cardHeight, galleryCardHeight, panelRect } from '../panels.ts';
import { layoutSig, modeSig, panelSig } from '../state.ts';
import { snapshotSig } from '../store.ts';
import {
  type CardModel,
  type CrateModel,
  cardKindOf,
  cardModel,
  type ExpansionModel,
  type GalleryModel,
  type GhostModel,
  type ItemModel,
  type MachineModel,
} from './model.ts';

/** Sends a card button's command; a refusal shows its reason bubble above the button. */
function send(cmd: Command, e: MouseEvent): boolean {
  const ctx = shellCtx();
  ctx.actions.click();
  const r = ctx.actions.dispatch(cmd);
  if (!r.ok) {
    const b = (e.currentTarget as HTMLElement).getBoundingClientRect();
    ctx.actions.refuse(r.reason, { x: b.x + b.width / 2, y: b.y });
  }
  return r.ok;
}

function Pips({ n, max }: { n: number; max: number }) {
  const out: JSX.Element[] = [];
  for (let i = 1; i <= max; i++) out.push(<i key={i} class={i <= n ? 'on' : ''} />);
  return <span class="pips">{out}</span>;
}

function Head(props: {
  icon: JSX.Element | null;
  title: string;
  sub?: string | null;
  tone?: string;
  level?: { n: number; max: number } | null;
  more?: boolean | null;
  /** The building type whose how-to card a "?" button opens (none without how-to copy). */
  help?: BType | null;
}) {
  const ctx = shellCtx();
  const c = ctx.data().copy.cards;
  const help = props.help ?? null;
  return (
    <div class="card-head">
      {props.icon}
      <div class="card-titles">
        <span class="card-title">
          {props.title}
          {props.level ? <Pips n={props.level.n} max={props.level.max} /> : null}
        </span>
        {props.sub ? (
          <span class={`card-status tone-${props.tone ?? 'ok'}`} data-testid="card-status">
            {props.sub}
          </span>
        ) : null}
      </div>
      {props.more !== undefined && props.more !== null ? (
        <button
          type="button"
          class="btn btn-soft ctl"
          data-hit="card-more"
          onClick={() => ctx.shell.toggleMore()}
        >
          {props.more ? (c.less ?? '') : (c.more ?? '')}
        </button>
      ) : null}
      {help !== null && ctx.shell.hasHowto(help) ? (
        <button
          type="button"
          class="icon-btn card-help ctl"
          data-hit="card-help"
          data-testid="card-help"
          aria-label={ctx.data().copy.howto.help ?? ''}
          onClick={() => {
            ctx.actions.click();
            ctx.shell.showHowto(help);
          }}
        >
          ?
        </button>
      ) : null}
      <button
        type="button"
        class="icon-btn ctl"
        data-hit="card-close"
        aria-label={c.close ?? ''}
        onClick={() => ctx.shell.closePanel()}
      >
        ✕
      </button>
    </div>
  );
}

function ItemCard({ m }: { m: ItemModel }) {
  const ctx = shellCtx();
  const px = ctx.data().ui.cards.iconPx;
  return (
    <>
      <Head
        icon={<Icon icon={m.icon} px={px} />}
        title={m.title}
        sub={m.value}
        level={{ n: m.tier, max: m.maxTier }}
      />
      <div class="card-lines">
        {m.mergeInto ? <span>{m.mergeInto}</span> : null}
        {m.usedBy ? <span>{m.usedBy}</span> : null}
      </div>
    </>
  );
}

function MachineCard({ m, more }: { m: MachineModel; more: boolean }) {
  const ctx = shellCtx();
  const c = ctx.data().copy.cards;
  const px = ctx.data().ui.cards.iconPx;
  const extra =
    m.lines.length > 0 || m.mergeHint !== null || m.packer !== null || m.advice !== null;
  return (
    <>
      <Head
        icon={<Icon icon={m.icon} px={px} />}
        title={m.title}
        sub={m.status}
        tone={m.tone}
        level={{ n: m.level, max: m.maxLevel }}
        more={extra ? more : null}
        help={m.type}
      />
      <div class="card-actions">
        {m.canRotate ? (
          <button
            type="button"
            class="btn ctl"
            data-hit="card-rotate"
            onClick={(e) => send({ c: 'rotate', bid: m.bid }, e)}
          >
            {'⟳︎'} {ctx.data().copy.build.rotate ?? ''}
          </button>
        ) : null}
        {m.pause !== null ? (
          <button
            type="button"
            class="btn ctl"
            data-hit="card-pause"
            onClick={(e) => send({ c: 'pause', bid: m.bid, on: m.pause === 'pause' }, e)}
          >
            {m.pause === 'pause' ? `⏸︎ ${c.pause ?? ''}` : `▶︎ ${c.resume ?? ''}`}
          </button>
        ) : null}
        {m.sell !== null && 'value' in m.sell ? (
          <button
            type="button"
            class="btn btn-buy ctl"
            data-hit="card-sell"
            onClick={(e) => {
              if (send({ c: 'sellBuilding', bid: m.bid }, e)) ctx.shell.closePanel();
            }}
          >
            {m.sell.text}
          </button>
        ) : null}
        {m.sell !== null && 'pinned' in m.sell ? (
          <span class="card-note">{m.sell.pinned}</span>
        ) : null}
        {m.editInBuild ? (
          <button
            type="button"
            class="btn btn-soft ctl"
            data-hit="card-edit"
            onClick={() => {
              ctx.actions.toggleBuild();
              ctx.shell.openCard({ k: 'building', id: m.bid });
            }}
          >
            {c.editInBuild ?? ''}
          </button>
        ) : null}
      </div>
      {more ? (
        <div class="card-more">
          {m.advice ? (
            <span class="advice" data-testid="machine-advice">
              {m.advice}
            </span>
          ) : null}
          {m.lines.map((x) => (
            <span key={x}>{x}</span>
          ))}
          {m.mergeHint ? <span>{m.mergeHint}</span> : null}
          {m.packer !== null ? (
            <div class="seg">
              <span class="seg-label">{c.target ?? ''}</span>
              {m.packer.options.map((t) => (
                <button
                  key={t}
                  type="button"
                  class={`seg-btn ctl ${t === m.packer?.target ? 'on' : ''}`}
                  data-hit={`packer-t${t}`}
                  onClick={(e) => send({ c: 'setTarget', bid: m.bid, tier: t }, e)}
                >
                  {fillCopy(c.packerTarget ?? 'T{n}', { n: t })}
                </button>
              ))}
            </div>
          ) : null}
          <span class="card-rule">{ctx.data().copy.rule.banner ?? ''}</span>
        </div>
      ) : null}
    </>
  );
}

function GalleryCard({ m }: { m: GalleryModel }) {
  const ctx = shellCtx();
  const gc = ctx.data().copy.gallery;
  const rowPx = ctx.data().ui.cards.rowIconPx;
  const s = ctx.state();
  const g = s.buildings.find((b) => b.id === m.bid);
  const slots: JSX.Element[] = [];
  for (let i = 0; i < m.queue.cap; i++) {
    const t = m.queue.tiers[i];
    slots.push(
      <span key={i} class={`ammo-slot ${t === undefined ? 'empty' : ''}`}>
        {t !== undefined ? <Icon icon={{ k: 'item', fam: 'ammo', tier: t }} px={rowPx} /> : null}
      </span>,
    );
  }
  return (
    <>
      <Head icon={null} title={m.title} />
      <div class="gallery-booth">
        <Icon
          icon={{ k: 'building', type: 'gallery', ore: null, level: g?.level ?? 1 }}
          px={ctx.data().ui.cards.boothPx}
        />
        {m.practice !== null ? (
          <span class="practice-tin">
            {gc.practice ?? ''} · {m.practice}
          </span>
        ) : null}
      </div>
      <div class="gallery-row">
        <b>{gc.gun ?? ''}</b>
        <span class="ammo-slot gun-slot">
          {m.gun ? <Icon icon={m.gun.icon} px={rowPx} /> : null}
        </span>
        <span data-testid="gallery-gun">{m.gun ? m.gun.line : (gc.noGunLine ?? '')}</span>
      </div>
      <div class="gallery-row">
        <b>{gc.ammo ?? ''}</b>
        <span class="ammo-queue">{slots}</span>
        <span>{m.queue.text}</span>
      </div>
      <div class="gallery-row">
        <b>{gc.income ?? ''}</b>
        <span>{m.income}</span>
      </div>
      <div
        class={`binding binding-${m.binding}`}
        data-testid="gallery-binding"
        data-binding={m.binding}
      >
        <b>{m.bindingText}</b>
        <span>{m.bindingLine}</span>
      </div>
      {m.tip ? <p class="card-line">{m.tip}</p> : null}
      {m.advice ? (
        <p class="card-line advice" data-testid="gallery-advice">
          {m.advice}
        </p>
      ) : null}
    </>
  );
}

function CrateCard({ m, more }: { m: CrateModel; more: boolean }) {
  const ctx = shellCtx();
  const c = ctx.data().copy.cards;
  const ui = ctx.data().ui;
  const px = ui.cards.iconPx;
  const rows = m.rows.map((r) => (
    <span key={r.tier} class="crate-row" title={r.name}>
      <Icon icon={r.icon} px={ui.cards.rowIconPx} />×{r.count}
    </span>
  ));
  return (
    <>
      <Head
        icon={<Icon icon={{ k: 'building', type: 'crate', ore: null, level: m.level }} px={px} />}
        title={m.title}
        sub={m.fill}
        level={{ n: m.level, max: ctx.data().sim.buildings.crate.capacity.length }}
        more={m.rows.length > 0 ? more : null}
        help="crate"
      />
      <div class="card-actions">
        <span class="crate-rows" data-testid="crate-rows">
          {rows.length > 0
            ? rows.slice(0, more ? rows.length : ui.cards.crateRowsCollapsed)
            : (c.empty ?? '')}
        </span>
        {m.sellAll !== null ? (
          // UX §2 / §7: a plain button, no confirm and no timed input — the undo toast protects the sale
          <button
            type="button"
            class="btn btn-buy ctl"
            data-hit="crate-sell-all"
            data-testid="crate-sell-all"
            onClick={(e) => {
              if (!send({ c: 'crateSellAll', bid: m.bid }, e)) return;
              // the Crate sale is undoable (R13): the same undo toast as a counter sale
              const u = canUndoSell(ctx.state(), ctx.data().sim);
              if (u.ok)
                ctx.shell.toast({
                  kind: 'undo',
                  text: fillCopy(ctx.data().copy.toast.sold ?? '', {
                    value: money(ctx.shell.fmt(), u.value),
                  }),
                  ms: ui.toasts.undoMs,
                  token: u.token,
                });
            }}
          >
            {m.sellAll.text}
          </button>
        ) : null}
        {m.sellBuilding !== null ? (
          <button
            type="button"
            class="btn ctl"
            data-hit="card-sell"
            onClick={(e) => {
              if (send({ c: 'sellBuilding', bid: m.bid }, e)) ctx.shell.closePanel();
            }}
          >
            {m.sellBuilding.text}
          </button>
        ) : null}
      </div>
    </>
  );
}

function GhostCard({ m }: { m: GhostModel }) {
  const ctx = shellCtx();
  const px = ctx.data().ui.cards.iconPx;
  const needPx = ctx.data().ui.cards.needIconPx;
  return (
    <>
      <Head
        icon={<Icon icon={m.icon} px={px} dim />}
        title={m.title}
        sub={m.paid}
        help={m.icon.k === 'building' ? m.icon.type : null}
      />
      <div class="card-actions">
        <span class="ghost-needs" data-testid="ghost-needs">
          {m.rows.map((r) => (
            <span key={r.text} class={`need ${r.done ? 'done' : ''}`}>
              <Icon icon={r.icon} px={needPx} />
              {r.done ? '✓' : ''} {r.text}
            </span>
          ))}
        </span>
        <button
          type="button"
          class="btn ctl"
          data-hit="ghost-cancel"
          onClick={(e) => {
            if (send({ c: 'cancelGhost', bid: m.bid }, e)) ctx.shell.closePanel();
          }}
        >
          {m.cancel}
        </button>
      </div>
    </>
  );
}

function ExpansionCard({ m }: { m: ExpansionModel }) {
  const ctx = shellCtx();
  const px = ctx.data().ui.cards.iconPx;
  const sub = m.price !== null && m.eta !== null ? `${m.price} · ${m.eta}` : null;
  return (
    <>
      <Head icon={<Icon frame="ui.lock" px={px} />} title={m.title} sub={sub} />
      <div class="card-actions">
        {m.block !== null ? <span class="card-note">{m.block}</span> : null}
        {m.id !== null && m.button !== null ? (
          <button
            type="button"
            class="btn btn-buy ctl"
            data-hit="expand"
            onClick={(e) => {
              if (m.id !== null && send({ c: 'expand', id: m.id }, e)) ctx.shell.closePanel();
            }}
          >
            {m.button}
          </button>
        ) : null}
      </div>
    </>
  );
}

function Body({ m, more }: { m: CardModel; more: boolean }) {
  switch (m.kind) {
    case 'item':
      return <ItemCard m={m} />;
    case 'machine':
      return <MachineCard m={m} more={more} />;
    case 'gallery':
      return <GalleryCard m={m} />;
    case 'crate':
      return <CrateCard m={m} more={more} />;
    case 'ghost':
      return <GhostCard m={m} />;
    case 'expansion':
      return <ExpansionCard m={m} />;
  }
}

export function CardPanel() {
  const l = layoutSig.value;
  const p = panelSig.value;
  void snapshotSig.value; // live contents at the snapshot rate
  const mode = modeSig.value?.mode ?? 'play';
  if (!l || p.k !== 'card') return null;
  const ctx = shellCtx();
  const s = ctx.state();
  const m = cardModel(ctx.shell.fmt(), s, p.target, mode);
  if (m === null) return null; // the entity is gone (sold, merged, consumed)
  const ui = ctx.data().ui;
  const kind = cardKindOf(s, p.target, mode);
  const h =
    kind === 'gallery' ? galleryCardHeight(l.Hs, ui.sheets) : cardHeight(l, ui.sheets, p.more);
  const r = panelRect(l, h);
  // "More" grows with its content up to sheets.moreRatio·Hs, anchored on the tab bar's top edge
  const style: JSX.CSSProperties = p.more
    ? {
        left: `${r.x}px`,
        width: `${r.w}px`,
        bottom: `calc(100% - ${l.tabs.y}px)`,
        minHeight: `${cardHeight(l, ui.sheets, false)}px`,
        maxHeight: `${r.h}px`,
      }
    : box(r);
  return (
    <div
      class={`card card-${m.kind} ctl ${p.more ? 'more' : ''}`}
      style={style}
      data-testid="card"
      data-card={m.kind}
    >
      <Body m={m} more={p.more} />
    </div>
  );
}
